import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse, HttpHeaders} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {Location} from '@angular/common';
import {environment} from '../environments/environment';
import {ObservableResult} from '../models/result.model';
import { EnvironmentCode } from '../environments/environment.model';

/**
 * Service for calling the API.
 */
@Injectable()
export class RestClientService {

  constructor(
    private httpClient: HttpClient
  ) {
  }


  /**
   * Makes a universal request.
   * @param method - the METHOD of request.
   * @param url - the URL to call.
   * @param body - the request body, optional.
   * @param params - the query parameters, optional.
   * @param headers - the request headers, optional.
   * @returns an observable which completes with the request
   */
  request<T = any>(
    method: any,
    url: string,
    body?: any,
    params?: { [name: string]: string },
    headers?: string | {
      [name: string]: string | string[];
    }
  ): Observable<HttpResponse<T>> {
    // Create http headers, using Auth header and cache headers
    const httpHeaders = this.mergeHeaders(headers ? new HttpHeaders(headers) : undefined);

    return this.observableWithRetry<T>(() => {
      return this.httpClient.request<T>(method, url, {
        body,
        observe: 'response',
        headers: httpHeaders,
        params
      });
    });
  }


  /**
   * Makes a universal request.
   * @param method - the METHOD of request.
   * @param url - the URL to call.
   * @param reqBody - the request body, optional.
   * @param params - the query parameters, optional.
   * @param headers - the request headers, optional.
   * @param isBlob - treat the response as a file
   * @param forDownload
   * @returns an observable which completes with the request
   */
  requestWithConstUrl<T = any>(
    method: any,
    url: string,
    reqBody: any = {},
    params?: { [name: string]: string },
    headers?: string | {
      [name: string]: string | string[];
    },
    isBlob?: boolean,
    forDownload?: boolean
  ): ObservableResult<T> {
    // Create http headers, using Auth header and cache headers
    if(!headers){
      headers = {};
    }
    headers['Cache-Control'] = 'no-cache';
    const httpHeaders = this.mergeHeaders(headers ? new HttpHeaders(headers) : undefined);

    httpHeaders.set('', 'no-cache');

    const hardcodedUrl = Location.joinWithSlash(
      environment.apiRootUrl || '', 'contacts/do-request'
    );


    reqBody = {
      uri: url,
      method,
      params: reqBody,
      headers: headers || {},
      forDownload: forDownload
    };

    if (environment.env !== EnvironmentCode.prod) {
      console.log('requestBody object -> ', reqBody);
    }

    // @ts-ignore
    return this.observableWithRetry<T>(() => {
      if (reqBody.uri === '/api/UserProfile/Appointments/'){
        reqBody.uri = reqBody.uri + reqBody.params;
        return this.httpClient.request<T>('POST', hardcodedUrl, {
          body: reqBody,
          observe: 'response',
          headers: httpHeaders,
          params,
          // @ts-ignore
          responseType: isBlob ? 'blob' : 'json'
        });
      }
      return this.httpClient.request<T>('POST', hardcodedUrl, {
        body: reqBody,
        observe: 'response',
        headers: httpHeaders,
        params,
        // @ts-ignore
        responseType: isBlob ? 'blob' : 'json'
      });
    }).pipe(
      switchMap(({body}) => {
        if (body instanceof Blob) {
          return ObservableResult.ofSuccess(body);
        }
        // @ts-ignore
        if (body.data.hasOwnProperty('status') && (body.data.status < 200 || body.data.status >= 400)) {
          // @ts-ignore
          return ObservableResult.ofError(body.data.title, body.data.status);
        }
        // @ts-ignore
        return ObservableResult.ofSuccess(body.data);
      }),
      catchError((err) => {
        if (typeof err.error?.data === 'string') {
          return ObservableResult.ofError(err.error?.data, err?.error?.status)
        }
        return ObservableResult.ofError(`${err.error?.data?.status} ${err.error?.data?.title}`, err?.error?.status)
      })
    );
  }

  /**
   * Makes a GET request.
   * @param url - the URL to call.
   * @param params - the query parameters, optional.
   * @param headers - the request headers, optional.
   * @returns an observable which completes with the request.
   */
  get<T>(
    url: string,
    params?: { [name: string]: string },
    headers?: { [name: string]: string | string[] }
  ): Observable<HttpResponse<T>> {
    // Create http headers, using Auth header and cache headers
    const httpHeaders = this.mergeHeaders(headers ? new HttpHeaders(headers) : undefined);

    return this.observableWithRetry<T>(() => this.httpClient.get<T>(url, {
      headers: httpHeaders,
      params,
      observe: 'response'
    }));
  }

  /**
   * Makes a POST request.
   * @param url - the URL to call.
   * @param body - the request body, optional.
   * @param params - the query parameters, optional.
   * @param headers - the request headers, optional.
   * @returns an observable which completes with the request
   */
  post<T = any>(
    url: string,
    body?: any,
    params?: { [name: string]: string },
    headers?: string | {
      [name: string]: string | string[];
    },
    responseType?: any,
  ): Observable<HttpResponse<T>> {
    // Create http headers, using Auth header and cache headers
    const httpHeaders = this.mergeHeaders(headers ? new HttpHeaders(headers) : undefined);

    return this.observableWithRetry<T>(() => {
      return this.httpClient.post<T>(url, body, {
        observe: 'response',
        headers: httpHeaders,
        params,
        responseType
      });
    });
  }

  /**
   * Makes a PATCH request.
   * @param url - the URL to call.
   * @param body - the request body, optional.
   * @param params - the query parameters, optional.
   * @param headers - the request headers, optional.
   * @returns an observable which completes with the request
   */
  patch<T = any>(
    url: string,
    body?: any,
    params?: { [name: string]: string },
    headers?: string | {
      [name: string]: string | string[];
    }
  ): Observable<HttpResponse<T>> {

    // Create http headers, using Auth header and cache headers
    const httpHeaders = this.mergeHeaders(headers ? new HttpHeaders(headers) : undefined);

    return this.observableWithRetry<T>(() => this.httpClient.patch<T>(url, body, {
      observe: 'response',
      headers: httpHeaders,
      params
    }));
  }

  /**
   * Makes a DELETE request.
   * @param url - the URL to call.
   * @param params - the query parameters, optional.
   * @param headers - the request headers, optional.
   * @returns an observable which completes with the request
   */
  delete<T = any>(
    url: string,
    params?: { [name: string]: string },
    headers?: { [name: string]: string | string[] } | HttpHeaders
  ): Observable<HttpResponse<T>> {

    return this.observableWithRetry<T>(() => this.httpClient.delete<T>(url, {
      observe: 'response',
      headers,
      params
    }));
  }

  /**
   * Makes a PUT request.
   * @param url - the URL to call.
   * @param body - the request body, optional.
   * @param params - the query parameters, optional.
   * @param headers - the request headers, optional.
   * @returns an observable which completes with the request
   */
  put<T = any>(
    url: string,
    body?: any,
    params?: { [name: string]: string },
    headers?: { [name: string]: string | string[] } | HttpHeaders
  ): Observable<HttpResponse<T>> {

    return this.observableWithRetry<T>(() => this.httpClient.put<T>(url, body, {
      observe: 'response',
      headers,
      params
    }));
  }

  /**
   * Merges different headers into a single headers instance.
   *
   * @param headersToMerge - the headers to merge.
   * @returns the merged headers instance.
   */
  private mergeHeaders(...headersToMerge: HttpHeaders[]): HttpHeaders {
    let mergedHeaders = new HttpHeaders();
    headersToMerge.filter(headers => !!headers)
      .forEach(headers => mergedHeaders = headers
        .keys()
        .map(key => ({key, value: headers.get(key)}))
        .reduce((headersAcc, header) => headersAcc.set(header.key, header.value), mergedHeaders));

    return mergedHeaders;
  }

  /**
   * Adds retry logic ot the request function.
   * If the request fails with error code 401 or 403(i.e. unauthorized, possibly because of token expiry)
   * the logic will try to reauthenticate the profileData and send the request again.
   *
   * If the re authentication failed or the request fails again profileData will be logged out.
   *
   * @param requestFunction contains the request logic
   */
  private observableWithRetry<T>(requestFunction: () => Observable<HttpResponse<T>>): Observable<HttpResponse<T>> {
    return requestFunction()
      .pipe(
        catchError((error: any) => {
          if (error.status === 401 || error.status === 403) {
            //  TODO: implement
          }

          return throwError(error);
        })
      );
  }

}
